import { Answer, AnswerType, LogoView } from '@packages/types'
import React from 'react'
import { styled } from 'styled-components'

import { useDispatch } from 'customizer/hooks'
import { setMobileDescription } from 'customizer/questionPanel/actions'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import RemoveIcon from 'icons/bold/01-Interface Essential/43-Remove-Add/remove-circle.svg'
import defaultThumbnailImg from 'img/variation.png'
import FileAnswer from 'themes/common/components/answers/FileAnswer'
import OutOfStockBadge from 'themes/common/components/answers/OutOfStockBadge'

import DescriptionButton from '../DescriptionButton'
import ImageAnswer from './ImageAnswer'

import './LogoAnswer.scss'

export interface LogoAnswerProps {
  answer: Answer
  onSelect: () => void
  onRemove: () => void
  selected?: boolean
  isRemovable?: boolean
  isOutOfStock?: boolean
}

interface RemoveButtonStyle {
  $styled: {
    removeButtonIconColor: string
    removeButtonColor: string
    removeButtonHoverColor: string
  }
}

const StyledRemoveButton = styled.div<RemoveButtonStyle>`
  background-color: ${props => props.$styled.removeButtonIconColor};

  svg {
    fill: ${props => props.$styled.removeButtonColor};
  }

  &:hover {
    svg {
      fill: ${props => props.$styled.removeButtonHoverColor};
    }
  }
`

const LogoAnswer = ({ onSelect, answer, selected, isRemovable, onRemove, isOutOfStock }: LogoAnswerProps) => {
  const dispatch = useDispatch()
  const answerStyle = useThemeSettings('answer')
  const fileUploadStyle = useThemeSettings('fileUpload')

  const handleAnswerRemove: React.MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    onRemove()
  }

  return (
    <div onClick={onSelect} className="answer-container">
      {answer.type === AnswerType.Asset && !answer.thumbnail ? (
        <FileAnswer
          {...answer}
          answerStyle={{ ...answerStyle, borderRadius: fileUploadStyle?.imageRounding }}
          selected={!!selected}
          extraClassNames="file-answer"
        />
      ) : (
        <ImageAnswer
          {...answer}
          value={
            answer.thumbnail?.url || (answer.views as LogoView[] | undefined)?.[0]?.logo?.url || defaultThumbnailImg
          }
          extraClassNames="logo-answer"
          selected={!!selected}
          answerStyle={{ ...answerStyle, borderRadius: fileUploadStyle?.imageRounding }}
          noResize={!(answer.views as LogoView[] | undefined)?.[0]?.logo?.url}
        />
      )}
      {isRemovable && (
        <StyledRemoveButton className="remove-button" $styled={fileUploadStyle} onClick={handleAnswerRemove}>
          <RemoveIcon aria-label="Remove icon" />
        </StyledRemoveButton>
      )}
      {isOutOfStock && <OutOfStockBadge />}

      {answer.showDescription && (
        <DescriptionButton
          isVisible={false}
          onClick={() => dispatch(setMobileDescription(answer))}
          className="description-button__logo-answer"
        />
      )}
    </div>
  )
}

export default LogoAnswer
